import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { X, Send, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { contactFormSchema, type ContactFormData } from "@/lib/validations";
import { FORM_OPTIONS } from "@/lib/constants/futurestation";
import { useWhatsApp } from "@/hooks/useWhatsApp";
import { Button } from "./button";
import { Input } from "./input";
import { Textarea } from "./textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { Label } from "./label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface ContactFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  triggerContext?: 'hero' | 'services' | 'contact';
}

type FormState = 'idle' | 'validating' | 'submitting' | 'success' | 'error';

const ContactFormModal = ({ isOpen, onClose, triggerContext = 'contact' }: ContactFormModalProps) => {
  const [formState, setFormState] = useState<FormState>('idle');
  const { openWhatsApp } = useWhatsApp();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    watch,
    setValue
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactFormSchema),
    mode: 'onChange',
    defaultValues: {
      nome: '',
      email: '',
      telefone: '',
      empresa: '',
      cargo: '',
      funcionarios: undefined,
      interesse: 'consultoria',
      mensagem: ''
    }
  });

  // Watch for real-time validation feedback
  const watchedFields = watch();

  const onSubmit = async (data: ContactFormData) => {
    setFormState('submitting');
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Log form data (in production, send to API)
      console.log('Form submitted:', data);
      
      setFormState('success');
      
      // Show success toast
      toast.success(
        "Formulário enviado com sucesso!",
        {
          description: "Redirecionando para WhatsApp em 3 segundos...",
          duration: 3000
        }
      );

      // Auto redirect to WhatsApp after success
      setTimeout(() => {
        const message = `Olá! Acabei de preencher o formulário de contato.

*Dados enviados:*
Nome: ${data.nome}
Empresa: ${data.empresa}
${data.cargo ? `Cargo: ${data.cargo}` : ''}
Interesse: ${FORM_OPTIONS.interests.find(i => i.value === data.interesse)?.label}
${data.funcionarios ? `Funcionários: ${FORM_OPTIONS.companySize.find(s => s.value === data.funcionarios)?.label}` : ''}
${data.mensagem ? `Mensagem: ${data.mensagem}` : ''}

Gostaria de agendar uma conversa!`;

        openWhatsApp('contact', message);
        handleClose();
      }, 3000);

    } catch (error) {
      setFormState('error');
      toast.error(
        "Erro ao enviar formulário",
        {
          description: "Tente novamente ou entre em contato pelo WhatsApp.",
          duration: 5000
        }
      );
    }
  };

  const handleClose = () => {
    reset();
    setFormState('idle');
    onClose();
  };

  const formatPhoneInput = (value: string) => {
    // Remove all non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Format as (XX) XXXXX-XXXX
    if (numbers.length <= 11) {
      let formatted = numbers;
      if (numbers.length >= 3) {
        formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
      }
      if (numbers.length >= 8) {
        formatted = `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
      }
      return formatted;
    }
    return value;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneInput(e.target.value);
    setValue('telefone', formatted);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto p-0">
        <div className="bg-white rounded-2xl">
          {/* Header */}
          <DialogHeader className="p-8 pb-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">
                  Solicitar Demonstração
                </DialogTitle>
                <p className="text-gray-600">
                  Preencha o formulário e nossa equipe entrará em contato
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </Button>
            </div>
          </DialogHeader>

          {/* Form */}
          <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
            {/* Success State */}
            {formState === 'success' && (
              <div className="flex items-center space-x-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <div>
                  <p className="text-green-800 font-medium">Formulário enviado com sucesso!</p>
                  <p className="text-green-600 text-sm">Redirecionando para WhatsApp...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {formState === 'error' && (
              <div className="flex items-center space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-6 h-6 text-red-600" />
                <div>
                  <p className="text-red-800 font-medium">Erro ao enviar formulário</p>
                  <p className="text-red-600 text-sm">Tente novamente ou contate pelo WhatsApp</p>
                </div>
              </div>
            )}

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Nome */}
              <div className="space-y-2">
                <Label htmlFor="nome" className="text-sm font-medium">
                  Nome completo *
                </Label>
                <Input
                  id="nome"
                  {...register('nome')}
                  placeholder="Seu nome completo"
                  className={cn(
                    "h-12",
                    errors.nome && "border-red-500 focus:border-red-500"
                  )}
                  disabled={formState === 'submitting' || formState === 'success'}
                />
                {errors.nome && (
                  <p className="text-sm text-red-600">{errors.nome.message}</p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Email corporativo *
                </Label>
                <Input
                  id="email"
                  type="email"
                  {...register('email')}
                  placeholder="seu.email@empresa.com"
                  className={cn(
                    "h-12",
                    errors.email && "border-red-500 focus:border-red-500"
                  )}
                  disabled={formState === 'submitting' || formState === 'success'}
                />
                {errors.email && (
                  <p className="text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              {/* Telefone */}
              <div className="space-y-2">
                <Label htmlFor="telefone" className="text-sm font-medium">
                  WhatsApp *
                </Label>
                <Input
                  id="telefone"
                  {...register('telefone')}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  className={cn(
                    "h-12",
                    errors.telefone && "border-red-500 focus:border-red-500"
                  )}
                  disabled={formState === 'submitting' || formState === 'success'}
                />
                {errors.telefone && (
                  <p className="text-sm text-red-600">{errors.telefone.message}</p>
                )}
              </div>

              {/* Empresa */}
              <div className="space-y-2">
                <Label htmlFor="empresa" className="text-sm font-medium">
                  Empresa *
                </Label>
                <Input
                  id="empresa"
                  {...register('empresa')}
                  placeholder="Nome da sua empresa"
                  className={cn(
                    "h-12",
                    errors.empresa && "border-red-500 focus:border-red-500"
                  )}
                  disabled={formState === 'submitting' || formState === 'success'}
                />
                {errors.empresa && (
                  <p className="text-sm text-red-600">{errors.empresa.message}</p>
                )}
              </div>

              {/* Cargo */}
              <div className="space-y-2">
                <Label htmlFor="cargo" className="text-sm font-medium">
                  Cargo
                </Label>
                <Input
                  id="cargo"
                  {...register('cargo')}
                  placeholder="Seu cargo na empresa"
                  className="h-12"
                  disabled={formState === 'submitting' || formState === 'success'}
                />
              </div>

              {/* Funcionários */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">
                  Número de funcionários
                </Label>
                <Select
                  onValueChange={(value) => setValue('funcionarios', value as "1-10" | "11-50" | "51-200" | "201-1000" | "1000+")}
                  disabled={formState === 'submitting' || formState === 'success'}
                >
                  <SelectTrigger className="h-12">
                    <SelectValue placeholder="Selecione o porte" />
                  </SelectTrigger>
                  <SelectContent>
                    {FORM_OPTIONS.companySize.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Interesse */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                Área de interesse *
              </Label>
              <Select
                onValueChange={(value) => setValue('interesse', value as "fluig-flows" | "desenvolvimento" | "outsourcing" | "integracao-totvs" | "consultoria")}
                disabled={formState === 'submitting' || formState === 'success'}
                defaultValue="consultoria"
              >
                <SelectTrigger className={cn(
                  "h-12",
                  errors.interesse && "border-red-500 focus:border-red-500"
                )}>
                  <SelectValue placeholder="Selecione seu interesse" />
                </SelectTrigger>
                <SelectContent>
                  {FORM_OPTIONS.interests.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.interesse && (
                <p className="text-sm text-red-600">{errors.interesse.message}</p>
              )}
            </div>

            {/* Mensagem */}
            <div className="space-y-2">
              <Label htmlFor="mensagem" className="text-sm font-medium">
                Mensagem adicional
              </Label>
              <Textarea
                id="mensagem"
                {...register('mensagem')}
                placeholder="Conte-nos mais sobre suas necessidades..."
                className={cn(
                  "min-h-[120px] resize-none",
                  errors.mensagem && "border-red-500 focus:border-red-500"
                )}
                disabled={formState === 'submitting' || formState === 'success'}
              />
              {errors.mensagem && (
                <p className="text-sm text-red-600">{errors.mensagem.message}</p>
              )}
            </div>

            {/* Submit Button */}
            <div className="flex space-x-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={formState === 'submitting' || formState === 'success'}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-fs-accent hover:bg-fs-accent/90"
                disabled={!isValid || formState === 'submitting' || formState === 'success'}
              >
                {formState === 'submitting' ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Enviando...
                  </>
                ) : formState === 'success' ? (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Enviado!
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Solicitar Demonstração
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormModal;